import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { EvaluationRatingAggregationQuery } from '@innovation-os-ng/common';
import { SessionService } from '@innovation-os-ng/core/auth';
import { FeatureFlagService } from '@innovation-os-ng/core/core';
import { FormatNumPipe } from '@innovation-os-ng/ui/utils';
import { CompoundFilter, Filter } from '@itonics/filters';
import { TranslateModule } from '@itonics/ngx-translate-simple';
import { NgSelectModule } from '@ng-select/ng-select';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { DefaultSignalFilterOptions } from '../../../constants/default-signal-filter';
import { EvaluationCountLimit, QueryByEvaluationLimit } from '../../../constants/evaluation-ratings';
import { Action, EndpointType, FeedEvaluationBasis, SignalFilterOption, SignalFilterOptionType } from '../../../interfaces/feed';
import { FilterSelected } from '../../../model/model';
import { EvaluationService } from '../../../services/evaluation.service';

@Component({
  selector: 'signals-filter-dropdown',
  templateUrl: './filter-dropdown.component.html',
  styleUrls: ['./filter-dropdown.component.scss'],
  standalone: true,
  imports: [CommonModule, NgSelectModule, TranslateModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilterDropdownComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  public actions: Array<Action>;

  @Output()
  public filterSelected: EventEmitter<FilterSelected> = new EventEmitter<FilterSelected>();

  @Input()
  public feedUri: string;

  @Input()
  public filter: Filter;

  @Input()
  public filterIdentifier: string;

  @Input()
  public evaluationBasis: FeedEvaluationBasis;

  // Set it to true if you want inline dropdown instead of normal dropdown
  @Input()
  public isInlineDropdown: boolean = false;

  private evaluationCountLoad: Subscription = new Subscription();
  private readonly evaluationChangeListner: Subscription = new Subscription();

  public filterableActions: Array<Action>;
  public selectedSignalFilterOption: SignalFilterOption;
  public signalFilterOptions: Array<SignalFilterOption> = [];
  public defaultSignalFilterOptions: Array<SignalFilterOption> = DefaultSignalFilterOptions;
  public isLoading: boolean;

  constructor(
    private readonly evaluationService: EvaluationService,
    private readonly sessionService: SessionService,
    private readonly cdr: ChangeDetectorRef,
    private readonly featureFlagService: FeatureFlagService,
    private readonly formatNumberPipe: FormatNumPipe
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.actions || changes.filter) {
      // Promise here because filterable actions can be feature flagged
      this.generateFilterableActions().then(() => this.loadCount());
    }
  }

  public ngOnDestroy(): void {
    this.destroyEvaluationCountLoad();
    this.destroyEvaluationChangelistner();
  }

  public ngOnInit(): void {
    // Promise here because filterable actions can be feature flagged
    this.generateFilterableActions().then(() => {
      this.selectedSignalFilterOption = this.signalFilterOptions[0];
      this.onFilterSelected(this.selectedSignalFilterOption);
      this.loadCount();
    });
  }

  private destroyEvaluationCountLoad(): void {
    if (this.evaluationCountLoad) {
      this.evaluationCountLoad.unsubscribe();
    }
  }

  private destroyEvaluationChangelistner(): void {
    if (this.evaluationChangeListner) {
      this.evaluationChangeListner.unsubscribe();
    }
  }

  private getQueryForFilterOption(filterOpt: SignalFilterOption): EvaluationRatingAggregationQuery {
    const query: EvaluationRatingAggregationQuery = {
      criterionUri: filterOpt.criterionUri,
      criterionValue: filterOpt.criterionValue,
      limit: EvaluationCountLimit
    };
    // Retrieving bad quality signals does not need and evaluation basis
    if (filterOpt?.includeEvaluationBasis) {
      if (this.evaluationBasis) {
        query[this.evaluationBasis.evaluationBasis] = this.evaluationBasis.uri;
      } else {
        const freshFilter = this.getFreshFilterByRemovingUnNecessaryFilter();
        query.searchQueryHash = this.evaluationService.generateUrlFriendlyHash(freshFilter);
      }
    }
    return query;
  }

  /**
   * Change the count of default filters
   *
   *
   *  */
  private loadCount(): void {
    this.destroyEvaluationCountLoad();

    const filterStickingOption = (opt: SignalFilterOption) => opt.showCount;

    // Only create filter for those which have criterion uri with it and is a sticking document.
    const ratingQuery: Array<EvaluationRatingAggregationQuery> = this.signalFilterOptions
      .filter((opt) => filterStickingOption(opt))
      .map((option) => this.getQueryForFilterOption(option));

    // Send http request
    this.evaluationCountLoad = this.evaluationService
      .getEvaluationCounts(ratingQuery)
      .pipe(take(1))
      .subscribe((data: { counts: Array<string> }) => {
        this.signalFilterOptions
          .filter((opt) => filterStickingOption(opt))
          .forEach((opt: SignalFilterOption, index) => {
            opt.value = data.counts ? data.counts[index] : 0;
          });
      });
  }

  /**
   * Fetch an array of the signal filter options that are enabled based on their individual feature flags
   * Any option without a specified feature flag will always be returned
   *
   * @returns (Promise with an) Array of signal filter options
   */
  private async getEnabledDefaultFilterOptions(): Promise<Array<SignalFilterOption>> {
    // Get an array of unique feature flags based on what's specified in the array of default options
    const featureFlags = this.defaultSignalFilterOptions.map((option) => option.featureFlag).filter(Boolean);
    // Deduplication
    const uniqueFeatureFlags = Array.from(new Set(featureFlags));

    // Fetch the status of which flags are enabled using featureFlagService
    const enabledFeatures = await Promise.all(uniqueFeatureFlags.map((flagName) => this.featureFlagService.isFeatureEnabled(flagName)));

    // Make a dictionary out of the two arrays above. Will have the form `featureFlag: true|false`
    const enabledFeaturesMap = uniqueFeatureFlags.reduce((featureMap, flagName, index) => {
      featureMap[flagName] = enabledFeatures[index];
      return featureMap;
    }, {});

    // Finally, use the map above to filter out any disabled filter options
    return this.defaultSignalFilterOptions.filter((option) => (option.featureFlag ? enabledFeaturesMap[option.featureFlag] : true));
  }

  /**
   * Populate the dropdown's options with a combination of the default actions
   * (after checking their feature flags) and the input `this.actions`
   *
   * @returns Promise<void>
   */
  private async generateFilterableActions(): Promise<void> {
    this.signalFilterOptions = await this.getEnabledDefaultFilterOptions();

    if (this.actions && this.actions.length) {
      this.filterableActions = this.actions.filter((action) => action.showAsFilter === true);
      this.actions.forEach((action) => {
        this.signalFilterOptions.splice(1, 0, {
          label: action.label,
          key: action.id,
          type: SignalFilterOptionType.ACTION,
          value: action.actionPerformedDocuments.length
        });
      });
      this.cdr.detectChanges();
      return;
    }
    this.filterableActions = [];
  }

  public onFilterSelected(action: SignalFilterOption): void {
    this.isLoading = false;
    if (action && action.loadValueByEndpoint) {
      this.getDocumentByRating(action);
    } else {
      this.emitFilterChanged({
        option: action || null
      });
    }
  }

  private getFreshFilterByRemovingUnNecessaryFilter(): Filter {
    const clonedFilter = this.filter.clone() as CompoundFilter;
    if (this.filterIdentifier) {
      const filterToRemove = clonedFilter.getQuery(this.filterIdentifier);
      if (filterToRemove) {
        clonedFilter.removeFilter(filterToRemove);
      }
    }
    return clonedFilter;
  }

  private getDocumentByRating(ratingType: SignalFilterOption): void {
    this.isLoading = true;

    const freshFilter = this.getFreshFilterByRemovingUnNecessaryFilter();

    const ratingQuery: EvaluationRatingAggregationQuery = {
      criterionUri: ratingType.criterionUri,
      criterionValue: ratingType.criterionValue,
      limit: QueryByEvaluationLimit
    };
    if (this.evaluationBasis) {
      ratingQuery[this.evaluationBasis.evaluationBasis] = this.evaluationBasis.uri;
    } else {
      ratingQuery.searchQueryHash = this.evaluationService.generateUrlFriendlyHash(freshFilter);
    }

    if (ratingType.endpointType === EndpointType.USER_RATED) {
      ratingQuery.minCount = 1;
      ratingQuery.userUri = this.sessionService.getUser().sub;
    }
    this.evaluationService
      .getTopDocumentUrisByRating(ratingQuery, ratingType.endpointType)
      .subscribe((results: { items: Array<{ uri: string }> }) => {
        this.isLoading = false;
        this.emitFilterChanged({
          option: ratingType,
          uris: results.items.map((x) => x.uri)
        });
      });
  }

  /**
   * Try to format the input with FormatNumPipe and return the output
   * If the input is a string, return the input directly
   *
   * @param value String or Number (string will be returned as-is, number will be formatted)
   * @returns (Formatted) input value as a string
   */
  public maybeFormatValue(value: string | number): string {
    return typeof value === 'number' ? this.formatNumberPipe.transform(value) : value;
  }

  /**
   * Emits the filter change output event and
   * signal category change for every listener.
   *
   * @param data - Selected filter data.
   */
  private emitFilterChanged(data: FilterSelected): void {
    this.filterSelected.emit(data);
  }
}
